import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Modal, Form, Input, Select, Tag, Space, 
  Popconfirm, message, Card, Row, Col, Avatar, Typography,
  Tooltip, Badge, Switch, Divider
} from 'antd';
import { 
  UserAddOutlined, EditOutlined, DeleteOutlined, 
  SearchOutlined, FilterOutlined, EyeOutlined,
  LockOutlined, MailOutlined, PhoneOutlined,
  TeamOutlined, ReloadOutlined, CheckCircleOutlined,
  CloseCircleOutlined, LoadingOutlined
} from '@ant-design/icons';
import axiosInstance from '../../Config/axiosConfig';

const { Option } = Select;
const { Title, Text } = Typography;

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);
  const [form] = Form.useForm();
  const [viewForm] = Form.useForm();
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    role: null,
    status: null,
  });

  // Pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Roles configuration
  const userRoles = [
    { id: 1, name: 'Admin', color: 'red', permissions: ['all'] },
    { id: 3, name: 'Teacher', color: 'blue', permissions: ['manage_classes', 'upload_content'] },
    { id: 4, name: 'Customer Care', color: 'green', permissions: ['manage_classes', 'upload_content'] },
    { id: 5, name: 'Academic', color: 'green', permissions: ['manage_classes', 'upload_content'] },
  ];
  useEffect(() => {
    fetchUsers();
  }, []);

// Fetch users from API with parameters
const fetchUsers = async (userType = 1, pageNumber = 1) => {
  setLoading(true);
  try {
    const accessToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem('deviceId');
    const storedUserIp = localStorage.getItem('userIp');

    console.log('check token', accessToken);
    
    // Request body with parameters
    const requestBody = {
      userType: userType,
      pageNumber: pageNumber
    };

    console.log('Request body:', requestBody);
    
    // Using POST method with parameters in request body
    const response = await axiosInstance.post(
      'Admin/get-users',
      requestBody,
      {  
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'UserDevice': storedDeviceId,
          'UserIPAddress': storedUserIp,
        },
      }
    );

    console.log('API Response:', response.data); // Debug log
    console.log('API Response structure:', JSON.stringify(response.data, null, 2));

    const data = response.data;

    if (data.code === 200 && data.data) {
      // Extract users array from the nested structure
      // Users are in data.data.users and each has userInfo and role
      const usersArray = data.data.users || [];
      const totalPages = data.data.pages || 0;
      
      console.log('Raw users array:', usersArray);
      console.log('Total pages:', totalPages);
      
      // Format users data from the nested structure
      const formattedUsers = usersArray.map((userItem, index) => {
        // Extract user info from the nested structure
        const userInfo = userItem.userInfo || {};
        const roleInfo = userItem.role || {};
        
        // Create a unique key
        const userKey = userInfo.id || userInfo.userId || `user-${index}-${Date.now()}`;
        
        return {
          key: userKey,
          id: userInfo.id || userInfo.userId,
          avatar: userInfo.avatar || null,
          name: userInfo.name || `${userInfo.firstName || ''} ${userInfo.lastName || ''}`.trim() || 'Unknown',
          email: userInfo.email || '',
          phone: userInfo.phone || userInfo.mobile || '',
          role: roleInfo.name || userInfo.role || userInfo.userRole || 'Unknown',
          roleId: roleInfo.id || userInfo.role_id || userInfo.roleId || userInfo.userRoleId,
          createdAt: userInfo.createdAt || userInfo.createdDate || '',
          lastLogin: userInfo.lastLogin || 'Never',
          coursesEnrolled: userInfo.coursesEnrolled || 0,
          totalSpent: userInfo.totalSpent || 0,
          // You might also want to store the raw data for debugging
          _rawUserInfo: userInfo,
          _rawRoleInfo: roleInfo
        };
      });

      console.log('Formatted users:', formattedUsers);

      setUsers(formattedUsers);
      setFilteredUsers(formattedUsers);
      
      // Update pagination with total count
      // If your API returns total count separately, use it, otherwise use formattedUsers.length
      setPagination(prev => ({ 
        ...prev, 
        total: data.data.total || data.data.totalCount || formattedUsers.length,
        pages: totalPages,
        current: pageNumber
      }));
      
      if (formattedUsers.length === 0) {
        message.info('No users found with the current filters');
      } else {
        message.success(`Loaded ${formattedUsers.length} users`);
      }
    } else {
      console.error('API Error:', data.message);
      message.error(data.message || 'Failed to load users');
      showSampleData();
    }
  } catch (error) {
    console.error('Error fetching users:', error);
    if (error.response) {
      console.error('Error response:', error.response);
      console.error('Error details:', {
        status: error.response.status,
        data: error.response.data,
        headers: error.response.headers
      });
      if (error.response.status === 401) {
        message.error('Unauthorized: Please login again');
      } else if (error.response.status === 403) {
        message.error('Forbidden: You do not have permission');
      } else {
        message.error(`Error ${error.response.status}: ${error.response.data?.message || 'Failed to load users'}`);
      }
    } else if (error.request) {
      console.error('No response received:', error.request);
      message.error('No response from server. Please check your network connection.');
    } else {
      console.error('Error setting up request:', error.message);
      message.error('Failed to make request. Please try again.');
    }
    showSampleData();
  } finally {
    setLoading(false);
  }
};

  // Sample data for development
  const showSampleData = () => {
    const sampleUsers = [
      {
        key: '1',
        id: 'USR-001',
        avatar: null,
        name: 'John Smith',
        email: 'john.smith@example.com',
        phone: '+1234567890',
        role: 'Admin',
        roleId: 1,
        status: 'active',
        createdAt: '2024-01-15',
        lastLogin: '2024-01-20 14:30:00',
        coursesEnrolled: 12,
        totalSpent: 1200,
      },
    ];

    setUsers(sampleUsers);
    setFilteredUsers(sampleUsers);
    setPagination(prev => ({ ...prev, total: sampleUsers.length }));
  };

  // Apply filters
  const applyFilters = () => {
    let result = [...users];

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(user => 
        user.name.toLowerCase().includes(searchLower) ||
        user.email.toLowerCase().includes(searchLower) ||
        user.phone?.toLowerCase().includes(searchLower) ||
        user.id.toLowerCase().includes(searchLower)
      );
    }

    // Role filter
    if (filters.role) {
      result = result.filter(user => user.role === filters.role);
    }

    // Status filter
    if (filters.status) {
      result = result.filter(user => user.status === filters.status);
    }

    setFilteredUsers(result);
    setPagination(prev => ({ ...prev, current: 1, total: result.length }));
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Reset filters
  const handleResetFilters = () => {
    setFilters({
      search: '',
      role: null,
      status: null,
    });
    setFilteredUsers(users);
    setPagination(prev => ({ ...prev, current: 1, total: users.length }));
  };

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [filters, users]);

  // Handle add user
  const handleAddUser = () => {
    setEditingUser(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Handle edit user
  const handleEditUser = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      status: user.status,
    });
    setModalVisible(true);
  };

  // Handle view user
  const handleViewUser = (user) => {
    setViewingUser(user);
    viewForm.setFieldsValue({
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      status: user.status,
      createdAt: user.createdAt,
      lastLogin: user.lastLogin,
      coursesEnrolled: user.coursesEnrolled,
      totalSpent: user.totalSpent,
    });
    setViewModalVisible(true);
  };

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const storedDeviceId = localStorage.getItem("deviceId");
      const storedUserIp = localStorage.getItem("userIp");

      const response = await axiosInstance.delete(
        `Admin/delete-user/${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'UserDevice': storedDeviceId,
            'UserIPAddress': storedUserIp,
          },
        }
      );

      if (response.data.code === 200) {
        message.success('User deleted successfully');
        fetchUsers();
      } else {
        throw new Error(response.data.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error(error.message || 'Failed to delete user. Please try again.');
    }
  };

 const handleSubmit = async (values) => {
  setSubmitLoading(true);

  try {
    const accessToken = localStorage.getItem("accessToken");
    const storedDeviceId = localStorage.getItem("deviceId");
    const storedUserIp = localStorage.getItem("userIp");

    if (editingUser) {
      // ======================
      // UPDATE USER
      // ======================
      const selectedRole = userRoles.find(r => r.name === values.role);
      if (!selectedRole) {
        throw new Error('Invalid role selected');
      }

      const payload = {
        name: values.name,
        email: values.email,
        roleId: selectedRole.id,
        status: values.status,
      };

      const response = await axiosInstance.put(
        `Admin/update-user/${editingUser.id}`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'UserDevice': storedDeviceId,
            'UserIPAddress': storedUserIp,
          },
        }
      );

      if (response.data?.code === 200) {
        message.success('User updated successfully');
        setModalVisible(false);
        fetchUsers();
      } else {
        throw new Error(response.data?.message || 'Update failed');
      }

    } else {
      // ======================
      // ADD USER (REGISTER) - Using multipart/form-data
      // ======================
      const selectedRole = userRoles.find(r => r.name === values.role);
      if (!selectedRole) {
        throw new Error('Invalid role selected');
      }

      // Create FormData for multipart/form-data
      const formData = new FormData();
      
      // Add required parameters
      formData.append('email', values.email);
      formData.append('name', values.name);
      formData.append('role', selectedRole.id.toString()); // Convert to string
      
      // Add optional fields
      if (values.phone) {
        formData.append('phone', values.phone);
      }
      // For instructor-specific fields (optional)
      const isInstructorRole = selectedRole.name === 'Teacher' || selectedRole.name === 'Academic';
      if (isInstructorRole) {
        if (values.instructorTitle) {
          formData.append('instructorTitle', values.instructorTitle);
        }
        if (values.aboutInstructor) {
          formData.append('aboutInstructor', values.aboutInstructor);
        }
        if (values.instructorTrainingEducationLevels && Array.isArray(values.instructorTrainingEducationLevels)) {
          values.instructorTrainingEducationLevels.forEach(level => {
            formData.append('instructorTrainingEducationLevels', level.toString());
          });
        }
      }

      // Log the form data for debugging
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(key + ': ' + (value instanceof File ? value.name + ' (' + value.type + ')' : value));
      }

      // Using POST with multipart/form-data
      const response = await axiosInstance.post(
        'Admin/register-user',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${accessToken}`,
            'UserDevice': storedDeviceId,
            'UserIPAddress': storedUserIp,
          },
        }
      );
      console.log('user added', response.data)
      if (response.data?.code === 200) {
        message.success('User registered successfully');
        setModalVisible(false);
        form.resetFields();
        fetchUsers();
      } else {
        throw new Error(response.data?.message || 'Registration failed');
      }
    }

  } catch (error) {
    console.error('Save user error:', error);

    if (error.response) {
      console.error('Error response:', error.response);
      if (error.response.status === 400) {
        message.error(error.response.data?.message || 'Bad request. Please check your input.');
      } else if (error.response.status === 401) {
        message.error('Unauthorized. Please login again.');
      } else if (error.response.status === 409) {
        message.error(error.response.data?.message || 'User already exists with this email.');
      } else {
        const msg = error.response.data?.message || `Error ${error.response.status}`;
        message.error(msg);
      }
    } else if (error.request) {
      message.error('No response from server. Please check your network connection.');
    } else {
      message.error(error.message || 'Network error. Please try again.');
    }

  } finally {
    setSubmitLoading(false);
  }
};


  // Handle status toggle
  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
      
      const accessToken = localStorage.getItem("accessToken");
      const storedDeviceId = localStorage.getItem("deviceId");
      const storedUserIp = localStorage.getItem("userIp");

      const response = await axiosInstance.put(
        `Admin/update-user-status/${userId}`,
        { status: newStatus },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'UserDevice': storedDeviceId,
            'UserIPAddress': storedUserIp,
          },
        }
      );

      if (response.data.code === 200) {
        message.success(`User status changed to ${newStatus}`);
        fetchUsers();
      } else {
        throw new Error(response.data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      message.error(error.message || 'Failed to update status. Please try again.');
    }
  };

  // Get role color
  const getRoleColor = (role) => {
    const roleConfig = userRoles.find(r => r.name === role);
    return roleConfig ? roleConfig.color : 'default';
  };

  // Get status tag
  const getStatusTag = (status) => {
    if (status === 'active') {
      return <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>;
    } else if (status === 'inactive') {
      return <Tag color="red" icon={<CloseCircleOutlined />}>Inactive</Tag>;
    } else if (status === 'pending') {
      return <Tag color="orange" icon={<LoadingOutlined />}>Pending</Tag>;
    }
    return <Tag color="default">{status}</Tag>;
  };

  // Table columns
  const columns = [
    {
      title: 'User',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text, record) => (
        <Space>
          <Avatar 
            size="small" 
            src={record.avatar}
            style={{ backgroundColor: '#1890ff' }}
          >
            {text?.charAt(0)}
          </Avatar>
          <div>
            <div style={{ fontWeight: 500 }}>{text}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.email}</Text>
          </div>
        </Space>
      ),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      render: (id) => <Text type="secondary">{id}</Text>,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone) => phone || 'N/A',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 120,
      render: (role) => (
        <Tag color={getRoleColor(role)}>{role}</Tag>
      ),
      filters: userRoles.map(role => ({
        text: role.name,
        value: role.name,
      })),
      onFilter: (value, record) => record.role === value,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status),
      filters: [
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ],
      onFilter: (value, record) => record.status === value,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleViewUser(record);
              }}
            />
          </Tooltip>
          <Tooltip title="Edit User">
            <Button 
              type="text" 
              icon={<EditOutlined />} 
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEditUser(record);
              }}
            />
          </Tooltip>
          <Tooltip title="Toggle Status">
            <Switch
              size="small"
              checked={record.status === 'active'}
              onChange={(checked, e) => {
                e.stopPropagation();
                handleToggleStatus(record.id, record.status);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete User"
            description="Are you sure you want to delete this user?"
            onConfirm={(e) => {
              e?.stopPropagation();
              handleDeleteUser(record.id);
            }}
            onCancel={(e) => e?.stopPropagation()}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete User">
              <Button 
                type="text" 
                danger
                icon={<DeleteOutlined />} 
                size="small"
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className='user-management-container'>
      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <TeamOutlined />
              <Title level={4} style={{ margin: 0 }}>User Management</Title>
              <Badge count={users.length} showZero style={{ backgroundColor: '#52c41a' }} />
            </Space>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={fetchUsers}
                loading={loading}
              >
                Refresh
              </Button>
              <Button 
                type="primary" 
                icon={<UserAddOutlined />}
                onClick={handleAddUser}
              >
                Add User
              </Button>
            </Space>
          </div>
        }
      >
        {/* Filters */}
        <div style={{ marginBottom: 20 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search by name, email, or ID"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Role"
                style={{ width: '100%' }}
                value={filters.role}
                onChange={(value) => handleFilterChange('role', value)}
                allowClear
              >
                {userRoles.map(role => (
                  <Option key={role.id} value={role.name}>
                    <Tag color={role.color}>{role.name}</Tag>
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Status"
                style={{ width: '100%' }}
                value={filters.status}
                onChange={(value) => handleFilterChange('status', value)}
                allowClear
              >
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Space>
                <Button 
                  onClick={applyFilters}
                  icon={<FilterOutlined />}
                  type="primary"
                >
                  Filter
                </Button>
                <Button 
                  onClick={handleResetFilters}
                  disabled={!filters.search && !filters.role && !filters.status}
                >
                  Clear
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Active Filters */}
          {(filters.search || filters.role || filters.status) && (
            <div style={{ marginTop: 16, padding: 8, backgroundColor: '#f0f5ff', borderRadius: 4 }}>
              <Space wrap>
                <Text type="secondary">Active Filters:</Text>
                {filters.search && (
                  <Tag color="blue">
                    Search: {filters.search}
                  </Tag>
                )}
                {filters.role && (
                  <Tag color="green">
                    Role: {filters.role}
                  </Tag>
                )}
                {filters.status && (
                  <Tag color={filters.status === 'active' ? 'green' : 'red'}>
                    Status: {filters.status}
                  </Tag>
                )}
              </Space>
            </div>
          )}
        </div>

        {/* Results Count */}
        <div style={{ marginBottom: 16 }}>
          <Text>
            Showing <strong>{filteredUsers.length}</strong> of <strong>{users.length}</strong> users
          </Text>
        </div>

        {/* Main Table */}
        <Table
          columns={columns}
          dataSource={filteredUsers}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
          }}
          scroll={{ x: 1200 }}
          bordered
        />
      </Card>

      {/* Add/Edit User Modal */}
      <Modal
        title={editingUser ? 'Edit User' : 'Add New User'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
        maskClosable={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            role: 'Student', // Default role
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Full Name"
                rules={[
                  { required: true, message: 'Please enter full name' },
                  { min: 3, message: 'Name must be at least 3 characters' }
                ]}
              >
                <Input 
                  prefix={<UserAddOutlined />} 
                  placeholder="Enter full name" 
                  disabled={!!editingUser}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter email' },
                  { type: 'email', message: 'Please enter valid email' }
                ]}
              >
                <Input 
                  prefix={<MailOutlined />} 
                  placeholder="Enter email address" 
                  disabled={!!editingUser}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="role"
                label="User Role"
                rules={[{ required: true, message: 'Please select role' }]}
              >
                <Select placeholder="Select user role">
                  {userRoles.map(role => (
                    <Option key={role.id} value={role.name}>
                      <Tag color={role.color}>{role.name}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {editingUser && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Please select status' }]}
                >
                  <Select placeholder="Select status">
                    <Option value="active">Active</Option>
                    <Option value="inactive">Inactive</Option>
                    <Option value="pending">Pending</Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          )}

          <Divider />

          <Form.Item>
            <Space style={{ float: 'right' }}>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={submitLoading}
              >
                {editingUser ? 'Update User' : 'Register User'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* View User Modal */}
      <Modal
        title="User Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        {viewingUser && (
          <Form
            form={viewForm}
            layout="vertical"
            disabled
          >
            <Row gutter={16}>
              <Col span={24}>
                <div style={{ textAlign: 'center', marginBottom: 24 }}>
                  <Avatar 
                    size={64} 
                    src={viewingUser.avatar}
                    style={{ backgroundColor: '#1890ff', marginBottom: 8 }}
                  >
                    {viewingUser.name?.charAt(0)}
                  </Avatar>
                  <Title level={4}>{viewingUser.name}</Title>
                  <Text type="secondary">{viewingUser.email}</Text>
                </div>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="User ID">
                  <Text strong>{viewingUser.id}</Text>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Phone">
                  <Text strong>{viewingUser.phone || 'N/A'}</Text>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Role">
                  <Tag color={getRoleColor(viewingUser.role)} size="large">
                    {viewingUser.role}
                  </Tag>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Status">
                  {getStatusTag(viewingUser.status)}
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Created Date">
                  <Text strong>
                    {new Date(viewingUser.createdAt).toLocaleDateString()}
                  </Text>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Last Login">
                  <Text strong>
                    {viewingUser.lastLogin === 'Never' ? 'Never' : new Date(viewingUser.lastLogin).toLocaleString()}
                  </Text>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="Courses Enrolled">
                  <Text strong>{viewingUser.coursesEnrolled}</Text>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Total Spent">
                  <Text strong type="success">
                    ${viewingUser.totalSpent?.toFixed(2) || '0.00'}
                  </Text>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default UserManagement;